'use client'

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'

interface ImageLightboxProps {
  imageUrl: string
  onClose: () => void
  /** When `video`, renders a `<video>` with controls instead of `<img>`. */
  mediaType?: 'image' | 'video'
}

export function ImageLightbox({ imageUrl, onClose, mediaType = 'image' }: ImageLightboxProps) {
  useEffect(() => {
    // Disable body scrolling when the lightbox is open
    document.body.style.overflow = 'hidden'
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape, true)
    
    return () => {
      document.removeEventListener('keydown', handleEscape, true)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999999] bg-black/90 backdrop-blur-md"
      onClick={onClose}
    >

      {/* Close button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className="absolute top-6 right-6 z-[99999999] text-white"
      >
        Close
      </Button>

      <div className="flex h-full justify-center items-center p-4">
        <div className="relative max-w-full max-h-full">
          {mediaType === 'video' ? (
            <video
              src={imageUrl}
              controls
              playsInline
              className="max-h-[85vh] max-w-full object-contain drop-shadow-xl"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={imageUrl}
              alt="Image preview"
              className="max-h-[85vh] max-w-full object-contain drop-shadow-xl"
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  )
} 